import { useState, useEffect } from 'react';

interface ExchangeRates {
  [currency: string]: number;
}

interface UseExchangeRatesReturn {
  rates: ExchangeRates;
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  convert: (amount: number, from: string, to: string) => number;
}

// Fallback rates in case API fails
const fallbackRates: ExchangeRates = {
  USD: 1,
  GBP: 0.79,
  EUR: 0.92,
  CAD: 1.36,
  AUD: 1.53,
  INR: 83.12,
  JPY: 149.50,
  BRL: 4.97,
  MXN: 17.15,
};

const CACHE_KEY = 'exchange_rates_cache';
const CACHE_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds

interface CachedRates {
  rates: ExchangeRates;
  timestamp: number;
}

export const useExchangeRates = (): UseExchangeRatesReturn => {
  const [rates, setRates] = useState<ExchangeRates>(fallbackRates);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  useEffect(() => {
    // Check cache first synchronously
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { rates: cachedRates, timestamp }: CachedRates = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_DURATION) {
          setRates(cachedRates);
          setLastUpdated(new Date(timestamp));
          setLoading(false);
          return;
        }
      }
    } catch {
      // Invalid cache, continue to fetch
    }

    // Use fallback rates immediately to avoid blocking render
    setRates(fallbackRates);
    setLoading(false);

    // Defer the actual API fetch to after page becomes interactive
    const fetchRates = async () => {
      setError(null);

      try {
        const response = await fetch(
          'https://api.exchangerate-api.com/v4/latest/USD'
        );

        if (!response.ok) {
          throw new Error('Failed to fetch exchange rates');
        }

        const data = await response.json();
        
        const newRates: ExchangeRates = {
          USD: 1,
          GBP: data.rates.GBP || fallbackRates.GBP,
          EUR: data.rates.EUR || fallbackRates.EUR,
          CAD: data.rates.CAD || fallbackRates.CAD,
          AUD: data.rates.AUD || fallbackRates.AUD,
          INR: data.rates.INR || fallbackRates.INR,
          JPY: data.rates.JPY || fallbackRates.JPY,
          BRL: data.rates.BRL || fallbackRates.BRL,
          MXN: data.rates.MXN || fallbackRates.MXN,
        };

        setRates(newRates);
        setLastUpdated(new Date());

        localStorage.setItem(CACHE_KEY, JSON.stringify({
          rates: newRates,
          timestamp: Date.now(),
        }));
      } catch {
        setError('Using cached exchange rates');
      }
    };

    // Use requestIdleCallback to defer fetch until browser is idle
    const scheduleDeferred = () => {
      if ('requestIdleCallback' in window) {
        (window as any).requestIdleCallback(() => fetchRates(), { timeout: 5000 });
      } else {
        // Fallback for Safari - defer by 2 seconds
        setTimeout(fetchRates, 2000);
      }
    };
    
    scheduleDeferred();

    // Refresh rates every hour
    const interval = setInterval(fetchRates, CACHE_DURATION);
    return () => clearInterval(interval);
  }, []);

  const convert = (amount: number, from: string, to: string): number => {
    const fromRate = rates[from] || 1;
    const toRate = rates[to] || 1;
    
    // Convert to USD first, then to target currency
    const inUSD = amount / fromRate;
    return inUSD * toRate;
  };

  return { rates, loading, error, lastUpdated, convert };
};
